<script setup>
//region Imports
import { ref, onMounted, reactive } from 'vue'
import config from '../config'
//endregion
//region Methods
const readData = () => JSON.parse(localStorage.getItem('tc-claim-data'))

const readIssue = () => JSON.parse(localStorage.getItem('tc-claim-issue'))

const readPhotos = () => JSON.parse(localStorage.getItem('tc-claim-photos'))

const readCompensation = () => JSON.parse(localStorage.getItem('tc-claim-compensation'))

const readSelectedItems = () => JSON.parse(localStorage.getItem('tc-claim-selected-items'))

const readShippingAddress = () => JSON.parse(localStorage.getItem('tc-claim-shipping-address'))

const readCustomerInformation = () => JSON.parse(localStorage.getItem('tc-claim-customer-information'))

const clickToCancel = () => {
  if (!confirm('Are you sure?')) {
    return
  }

  localStorage.removeItem('tc-claim-issue')
  location.replace('#/order')
}

const editOrder = () => location.replace('#/')

const editIssue = () => location.replace('#/claim/start')
const editPhotos = () => location.replace('#/claim/photos')
const editAdditionalDetails = () => location.replace('#/claim/other')
const editShippingAddress = () => location.replace('#/claim/shipping')
const editCompensation = () => location.replace('#/claim/compensation')

const submitClaim = () => {
  if (!checkbox.value) {
    alert('Please certify that any information you submit is truthful and accurate.')
    return
  }

  submitting.value = true

  fetch(`${config.cloud.url}/api/shopify/order/shipping-protection/claims`, {
    mode: 'cors',
    method: 'POST',
    cache: 'no-cache',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reason: issue?.key,
      orderId: data?.shopifyOrderId,
      userAgent: navigator.userAgent,
      orderNumber: orderNumber?.value,
      compensation: compensation?.key,
      customerNotes: issue?.customerNotes,
      shippingAddress: data.shippingAddress,
      items: items.map(item => ({
        poNumber: item.poNumber,
        lineItemId: item.lineItemId,
      })),
      customerEmail: customerInformation?.email ?? data?.customerEmail,
      customerName: customerInformation?.firstName + ' ' + customerInformation?.lastName,
    }),
  }).
      then(response => response.json()).
      then(data => {
        if (!data?.success) {
          alert('An error occurred while submitting your claim. Please try again later.')
          submitting.value = false
          return data
        }

        localStorage.setItem('tc-claim-success', JSON.stringify(data))
        location.replace('#/claim/success')
      }).
      catch(error => console.error(new Error(error)))
}

//endregion
//region Variables
const data = reactive(readData() ?? {})
const issue = reactive(readIssue() ?? {})
const photos = reactive(readPhotos() ?? {})
const items = reactive(readSelectedItems() ?? [])
const compensation = reactive(readCompensation() ?? {})
const shippingAddress = reactive(readShippingAddress() ?? {})
const customerInformation = reactive(readCustomerInformation() ?? {})

const checkbox = ref(false)
const submitting = ref(false)
const orderNumber = ref(data?.orderNumber)
//endregion
//region Lifecycle
onMounted(() => {
  if (!orderNumber.value) {
    location.replace('/')
  }
})
//endregion
</script>

<template>

  <header class="bg-tc-off-white-two overflow-hidden flex items-center justify-center h-[280px]">
    <div class="relative min-w-[171px] h-[171px]">
      <img :src="items[0]?.image" class="w-[143px] shadow z-50 absolute top-0 right-0" alt="image"/>
      <div v-if="items.length > 1">
        <span class="absolute bg-black rounded-full text-white text-tc-subtitle font-medium font-avenir w-[27px] h-[27px] flex justify-center items-center z-50 -top-1.5 -right-1.5"
              v-text="items.length"></span>
        <img :src="items[1]?.image ?? items[0]?.image" class="w-[143px] shadow z-20 absolute top-2 right-2" alt="image"/>
        <img v-if="items.length > 2" :src="items[2]?.image ?? items[0]?.image"
             class="w-[143px] shadow z-10 absolute top-4 right-4" alt="image"/>
      </div>
    </div>
  </header>

  <div v-if="submitting" class="flex flex-col items-center justify-center text-center p-4 min-h-[280px]">
    <figure class="mx-auto">
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="49" viewBox="0 0 25 49" fill="none">
        <path d="M10.1231 39.5608V10.4135C10.1533 10.413 10.1817 10.4068 10.2113 10.4068C16.407 10.4068 20.5372 16.0344 21.9139 24.1737C25.8018 22.3518 25.6789 18.3835 23.4113 15.6703C20.497 12.1477 15.7999 10.1231 10.2113 10.1231C10.1812 10.1231 10.1533 10.1265 10.1231 10.1265V0H9.83937C9.83937 8.86811 5.10204 10.1231 0 10.1231V10.4068H5.26289V40.2489C5.26289 44.2183 8.01638 48.5909 14.3745 48.5909C18.5461 48.5909 21.3392 45.3118 22.4322 43.4073L22.1898 43.2861C21.0158 45.3118 18.4645 47.7 15.1442 47.7C11.0139 47.7 10.1231 44.3395 10.1231 39.5608Z"
            fill="#2E1319"/>
      </svg>
    </figure>
    <h1 class="text-xl font-bold text-center mt-[20px]">Reviewing your claim</h1>
  </div>

  <main class="px-[20px] mt-[30px] lg:px-[40px] lg:max-w-[420px] lg:mx-auto" v-if="!submitting">

    <h1 class="text-tc-subtitle font-bold text-tc-black font-minionpro tracking-wider mb-[30px]">Review Claim Details</h1>

    <ul class="grid grid-cols-1 gap-5">

      <li v-if="orderNumber">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-lg tracking-wide mb-[12px]">Order</h2>
            <p class="text-black font-avenir text-tc-xl font-normal tracking-wide mb-[19px]" v-text="orderNumber"></p>
          </div>
          <button @click="editOrder"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li v-if="issue?.title">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-lg tracking-wide mb-[12px]">Issue Reported</h2>
            <p class="text-black font-avenir text-tc-xl font-normal tracking-wide mb-[19px]" v-text="issue?.title"></p>
          </div>
          <button @click="editIssue"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li v-if="photos.length">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-xl tracking-wide mb-[19px]">Photos</h2>
          </div>
          <button @click="editPhotos"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
          <ul class="flex flex-row gap-3">
            <li v-for="photo in photos">
              <img :src="photo" alt="photo">
            </li>
          </ul>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li v-if="issue?.customerNotes">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-lg tracking-wide mb-[12px]">Additional Details</h2>
            <p class="text-black font-avenir text-tc-xl font-normal tracking-wide mb-[19px]" v-text="issue?.customerNotes"></p>
          </div>
          <button @click="editAdditionalDetails"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li v-if="compensation?.title">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-lg tracking-wide mb-[12px]">Compensation</h2>
            <p class="text-black font-avenir text-tc-xl font-normal tracking-wide mb-[19px]" v-text="compensation?.title"></p>
          </div>
          <button @click="editCompensation"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li v-if="shippingAddress?.street">
        <div class="flex justify-between items-baseline w-full">
          <div class="max-w-[85%]">
            <h2 class="text-tc-muted-brown font-normal font-avenir text-tc-lg tracking-wide mb-[12px]">Shipping Address</h2>
            <p class="text-black font-avenir text-tc-xl font-normal tracking-wide mb-[19px]" v-text="shippingAddress?.formatted"></p>
          </div>
          <button @click="editShippingAddress"
                  class="text-black text-center font-avenir text-tc-lg font-normal tracking-wide bg-tc-off-white py-2 px-4"
          >Edit</button>
        </div>
        <hr class="bg-tc-off-white-two">
      </li>

      <li class="mt-[7px]">
        <label class="flex items-start gap-4">
          <input type="checkbox"
                 class="!w-[18px] !h-[18px] border border-solid border-black !ring-0 !outline-none !shadow-none text-tc-lg text-black tracking-wider mb-[20px] leading-normal"
                 v-model="checkbox"
          >I certify that any information I submit is truthful and accurate.
        </label>
        <p class="text-tc-greek-blue text-tc-lg leading-normal tracking-wider">WARNING: Providing false or misleading information to Tres Colori
          to try to obtain compensation to which one is not entitled to is insurance fraud.</p>
      </li>

    </ul>

    <div class="my-[30px] flex flex-col lg:flex-row justify-between gap-5">
      <button
          type="button"
          @click="submitClaim"
          class="text-tc-xl font-avenir font-normal tracking-wider border py-2.5 px-8 bg-tc-maroon text-white border-tc-maroon"
      >Submit Claim
      </button>
      <button
          type="button"
          @click="clickToCancel"
          class="text-tc-xl font-avenir font-normal tracking-wider py-2.5 px-8 border border-tc-creme text-tc-creme"
      >Cancel
      </button>
    </div>

  </main>


</template>

<style scoped>
</style>